import { useCallback, useState } from 'react';
import { usePage } from '@inertiajs/react';
import { apiPut, apiPost, apiDelete } from '../client';
import { useNotesStore } from '../../store/notesStore';
import type { ApiNote, UpdateNoteData, CreateNoteData, UpdateNoteResponse } from './types';
import { useNoteTabs } from '@/contexts/NoteTabsContext';

import { mapApiToNote } from '@/api/notes/noteUtils';
import { prepareNoteBody } from '@/services/NoteBodyService';
import { useMasterPasswordStore } from '@/store/masterPasswordStore';
import { cacheNoteBody } from '@/services/NoteBodyCache';

const noteWriteTails = new Map<string, Promise<unknown>>();
const noteWriteGenerations = new Map<string, number>();
const noteWriteSequences = new Map<string, number>();
const latestNoteResponses = new Map<string, ApiNote>();

export function synchronizeNoteWriteSequence(noteId: string, seq: number): void {
  noteWriteSequences.set(noteId, seq);
}

export function hasPendingNoteWrite(noteId: string): boolean {
  return noteWriteTails.has(noteId);
}

async function serializeNoteWrite<T extends { note?: ApiNote }>(
  key: string,
  generation: number,
  fallbackSeq: number,
  operation: (seq: number) => Promise<T>,
): Promise<T | null> {
  const previous = noteWriteTails.get(key) ?? Promise.resolve();
  const task = previous.catch(() => undefined).then(async () => {
    if (generation < (noteWriteGenerations.get(key) ?? generation)) return null;
    const storeSeq = useNotesStore.getState().notes.find((note) => note.id === key)?.seq;
    const result = await operation(noteWriteSequences.get(key) ?? storeSeq ?? fallbackSeq);
    if (result.note) {
      if (result.note.seq !== undefined) noteWriteSequences.set(key, result.note.seq);
      latestNoteResponses.set(key, result.note);
    }
    return result;
  });
  const tail = task.then(() => undefined, () => undefined);
  noteWriteTails.set(key, tail);
  void tail.then(() => {
    if (noteWriteTails.get(key) === tail) noteWriteTails.delete(key);
  });
  return task;
}

export function queueNoteBodyUpdate(
  noteId: string,
  data: UpdateNoteData,
  fallbackSeq: number,
  keepalive = false,
): Promise<UpdateNoteResponse | null> {
  const generation = noteWriteGenerations.get(noteId) ?? 0;
  return serializeNoteWrite<UpdateNoteResponse>(noteId, generation, fallbackSeq, async (seq) => {
    const response = await fetch(`/api/app/v1/note/${noteId}`, {
      method: 'PUT',
      credentials: 'include',
      keepalive,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ ...data, next_seq: seq + 1 }),
    });
    let result: UpdateNoteResponse;
    try {
      result = await response.json();
    } catch {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }
    if (!response.ok) throw new Error((result as any)?.message || `HTTP ${response.status} ${response.statusText}`);
    return result;
  });
}

export function queueNoteCommit(noteId: string): Promise<unknown | null> {
  const generation = noteWriteGenerations.get(noteId) ?? 0;
  const fallbackSeq = useNotesStore.getState().notes.find((note) => note.id === noteId)?.seq ?? 0;
  return serializeNoteWrite(noteId, generation, fallbackSeq, async () => {
    const response = await fetch(`/api/app/v1/note/${noteId}/commit`, {
      method: 'POST', credentials: 'include', headers: { 'Accept': 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`);
    return response.json();
  });
}

export function useManageNote() {
  const { encryptionPreference } = usePage<{ encryptionPreference?: { enabled: boolean } | null }>().props;
  const updateNote = useNotesStore((s) => s.updateNote);
  const addNote = useNotesStore((s) => s.addNote);
  const deleteNote = useNotesStore((s) => s.deleteNote);
  const { removeTab, updateTabTitle } = useNoteTabs();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateOrCreateNote = useCallback(
    async (noteId: string | null | undefined, data: UpdateNoteData | CreateNoteData) => {
      setLoading(true);
      setError(null);
      const isCreating = !noteId;
      const localNote = noteId
        ? useNotesStore.getState().notes.find((item) => item.id === noteId)
        : undefined;
      const requestedMode = data.encrypted ?? localNote?.encrypted;
      const encryptionEnabled = Boolean(requestedMode ?? (isCreating ? encryptionPreference?.enabled : false));
      const stableNoteId = noteId ?? (data as CreateNoteData).note_id ?? crypto.randomUUID();
      const operationData: any = { ...data, ...(isCreating ? { note_id: stableNoteId } : {}) };
      const requestData = { ...operationData };
      delete requestData.seq;
      if (isCreating) {
        requestData.encrypted = encryptionEnabled;
      }
      const isContentWrite = typeof operationData.content === 'string';
      const writeGeneration = isContentWrite
        ? (noteWriteGenerations.get(stableNoteId) ?? 0) + 1
        : (noteWriteGenerations.get(stableNoteId) ?? 0);
      if (isContentWrite) noteWriteGenerations.set(stableNoteId, writeGeneration);

      try {
        let apiNote: ApiNote;
        let versionsPruned = 0;
        let outgoingData: any = { ...requestData };
        const effectiveNoteId = stableNoteId;
        if (effectiveNoteId && typeof outgoingData.content === 'string') {
          try {
            const prepared = await prepareNoteBody(effectiveNoteId, outgoingData.content, encryptionEnabled, isCreating);
            outgoingData.content = prepared.content;
            if (prepared.keyWrapper) outgoingData.key_wrapper = prepared.keyWrapper;
            if (isCreating) outgoingData.note_id = effectiveNoteId;
          } catch (cryptoError: any) {
            const master = useMasterPasswordStore.getState();
            const retryData = { ...operationData, ...(effectiveNoteId ? { note_id: effectiveNoteId } : {}) };
            const retry = () => { void updateOrCreateNote(noteId, retryData as any).then(resolveRetry, rejectRetry); };
            let resolveRetry: (value: any) => void = () => {};
            let rejectRetry: (reason?: any) => void = () => {};
            const retryResult = new Promise<any>((resolve, reject) => { resolveRetry = resolve; rejectRetry = reject; });
            if (!master.masterKey && master.isConfigured) master.openUnlockModal(retry);
            else if (!master.masterKey) master.openModal(retry);
            else throw cryptoError;
            setLoading(false);
            return retryResult;
          }
        }

        if (!noteId) {
          const response = await serializeNoteWrite<{ note: ApiNote }>(stableNoteId, writeGeneration, 0, () => apiPost<{ note: ApiNote }>(
            `/app/v1/note`,
            { ...outgoingData, encrypted: encryptionEnabled }
          ));
          if (!response) {
            const previous = latestNoteResponses.get(stableNoteId);
            if (previous) return mapApiToNote(previous);
            throw new Error('A newer save replaced this request. Your latest note is still being created.');
          }
          apiNote = response.note;
        } else {
          const baselineSeq = (data as UpdateNoteData).seq
            ?? useNotesStore.getState().notes.find((item) => item.id === noteId)?.seq
            ?? 0;
          const response = await serializeNoteWrite<UpdateNoteResponse>(stableNoteId, writeGeneration, baselineSeq, (seq) => apiPut<UpdateNoteResponse>(
            `/app/v1/note/${noteId}`,
            { ...outgoingData, ...(requestedMode !== undefined ? { encrypted: encryptionEnabled } : {}), next_seq: seq + 1 }
          ));
          if (!response) {
            const previous = latestNoteResponses.get(stableNoteId);
            if (previous) return mapApiToNote(previous);
            const local = useNotesStore.getState().notes.find((item) => item.id === noteId);
            if (local) return local;
            throw new Error('A newer save replaced this request. Your latest text is still being saved.');
          }
          apiNote = response.note;
          if (apiNote.seq !== undefined) noteWriteSequences.set(stableNoteId, apiNote.seq);
          versionsPruned = response.versions_pruned ?? 0;
        }

        const updatedNote = mapApiToNote(apiNote);
        if (typeof (data as any).content === 'string') {
          updatedNote.content = (data as any).content;
          updatedNote.body_locked = false;
          if (updatedNote.encrypted && updatedNote.id) {
            cacheNoteBody(updatedNote.id, updatedNote.seq ?? 0, (data as any).content);
            if (updatedNote.content_id !== undefined) {
              cacheNoteBody(updatedNote.id, updatedNote.seq ?? 0, (data as any).content, updatedNote.content_id);
            }
          }
        }
        else if (updatedNote.encrypted) {
          const localBody = useNotesStore.getState().notes.find((item) => item.id === updatedNote.id)?.content;
          if (localBody && !localBody.startsWith('memoslate-body:v1:')) updatedNote.content = localBody;
        }
        if (requestedMode !== undefined || isCreating) {
          updatedNote.encrypted = encryptionEnabled;
        }
        updatedNote.versions_pruned = versionsPruned;
        if (!noteId) {
          const hasLocalDraft = useNotesStore.getState().notes.some((note) => note.id === null);

          if (hasLocalDraft) {
            updateNote(null, updatedNote);
          } else {
            addNote(updatedNote);
          }
        } else {
          updateNote(noteId, updatedNote);
        }

        // Update tab title when note is updated/created
        if (updatedNote.id && updatedNote.title !== undefined) {
          updateTabTitle(updatedNote.id, updatedNote.title || 'Untitled');
        }

        return updatedNote;
      } catch (e: any) {
        const errorMessage = e?.message || (noteId ? 'Failed to update note' : 'Failed to create note');
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [addNote, updateNote, updateTabTitle]
  );

  const deleteNoteById = useCallback(
    async (noteId: string | null) => {
      setLoading(true);
      setError(null);

      try {
        if (noteId) {
          await apiDelete(`/app/v1/note/${noteId}`);
          noteWriteSequences.delete(noteId);
          removeTab(noteId); // Remove tab when note is deleted
        }
        deleteNote(noteId);
      } catch (e: any) {
        const errorMessage = e?.message || 'Failed to delete note';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [deleteNote, removeTab]
  );

  /**
   * Commit the current draft for a note (fire-and-forget).
   * Used on navigate away when there are no pending content changes but an
   * active draft exists from earlier autosaves in the session.
   * Errors are swallowed — the cron job is the safety net.
   */
  const commitNote = useCallback((noteId: string) => {
    apiPost(`/app/v1/note/${noteId}/commit`).catch(() => {});
  }, []);

  return { updateOrCreateNote, deleteNoteById, commitNote, loading, error };
}
